import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { projectsListService } from '../services/projectsList.service';
import type { ProjectFiltersState } from '../types/projects1.types';

const defaultFilters: ProjectFiltersState = {
  search: '',
  type: 'all',
  status: 'all',
  region: 'all',
  page: 1,
  pageSize: 10,
};

export function useProjectsList() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [filters, setFilters] = useState<ProjectFiltersState>(defaultFilters);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['projects1', 'list', companyId, filters],
    queryFn: () => projectsListService.getDashboard(companyId, filters),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectsListService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects1'] });
    },
  });

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleDeleteProject = useCallback(async (id: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa dự án này? Thao tác này không thể hoàn tác.')) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('[useProjectsList] Delete failed:', error);
      alert('Có lỗi xảy ra khi xóa dự án.');
    }
  }, [deleteMutation]);

  return {
    data: query.data || { 
      projects: [], 
      upcoming: [],
      total: 0, 
      summary: { 
        total: 0, construction: 0, maintenance: 0, active: 0, completed: 0, planning: 0, paused: 0, totalContract: 0,
        totalContractConstruction: 0, totalContractMaintenance: 0 
      } 
    },
    filters,
    setFilters,
    isLoading: query.isLoading,
    isError: query.isError,
    handlePageChange,
    handleDeleteProject,
  };
}
